import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Container, Link, SectionText } from '@/atoms';
import { CardBlog } from '@/molecules';
import { Blog } from '@/organisms';

import type { FunctionComponent } from 'react';
import type { TypeCardsBlogTemplateProps } from './types';

const SwiperCustom = dynamic(() => import('@/molecules/swiper-custom'));

/**
 * CardsBlogTemplate
 */
export const CardsBlogTemplate: FunctionComponent<
  TypeCardsBlogTemplateProps
> = ({
  className = '',
  backgroundColorBottom,
  backgroundColorBottomMobile,
  backgroundColorTop,
  backgroundColorTopMobile,
  caption,
  cutLevel = 50,
  cutLevelMobile = 50,
  desc,
  textColor,
  title,
  blogProps,
  link,
  isCarrousel = false,
  isMobileCarrousel = false,
  id,
}: TypeCardsBlogTemplateProps) => (
  <Container
    backgroundColorBottom={backgroundColorBottom}
    backgroundColorTopMobile={backgroundColorTopMobile}
    backgroundColorTop={backgroundColorTop}
    backgroundColorBottomMobile={backgroundColorBottomMobile}
    cutLevel={cutLevel === null ? 50 : cutLevel}
    cutLevelMobile={cutLevelMobile === null ? 50 : cutLevel}
    className={`flex flex-col gap-10 ${className}`}
    id={id}
    {...(isCarrousel &&
      !isMobileCarrousel && {
        overflows: true,
      })}
  >
    <div className="flex flex-col gap-10 lg:flex-row lg:gap-20">
      <div
        className={oneLine`
          flex flex-col gap-14
          ${
            isMobileCarrousel
              ? 'lg:w-1/3 md:lg:w-2/3'
              : isCarrousel
              ? 'lg:w-1/3'
              : 'lg:w-2/3'
          }
        `}
      >
        <SectionText
          className="justify-center "
          direction="column"
          textAlign="start"
          title={title}
          desc={desc}
          caption={caption}
          variant={textColor}
        />

        {link?.uri && (
          <NextLink href={link.uri} passHref prefetch={false}>
            <Link
              className="hidden lg:flex"
              variant={link.variant}
              underline
              arrow
            >
              {link.label}
            </Link>
          </NextLink>
        )}
      </div>

      {Array.isArray(blogProps) && !isCarrousel && (
        <Blog
          {...(blogProps?.at(0) && {
            cardAbove: blogProps[0],
          })}
          {...(blogProps?.at(1) && {
            cardBelow: blogProps[1],
          })}
          {...(blogProps?.at(2) && {
            cardLeft: blogProps[2],
          })}
        />
      )}

      {isCarrousel && !isMobileCarrousel && (
        <div className="mx-4 md:mx-0 lg:max-w-[66%]">
          <SwiperCustom
            scrollbar
            navigation
            slidesPerView={3}
            slidesPerViewMobile={1.3}
            gap={24}
          >
            {blogProps?.map((blog, index) => (
              <CardBlog {...blog} key={index} className="h-full" />
            ))}
          </SwiperCustom>
        </div>
      )}

      {isMobileCarrousel && (
        <>
          <div className="hidden md:block">
            {' '}
            <Blog
              {...(blogProps?.at(0) && {
                cardAbove: blogProps[0],
              })}
              {...(blogProps?.at(1) && {
                cardBelow: blogProps[1],
              })}
              {...(blogProps?.at(2) && {
                cardLeft: blogProps[2],
              })}
            />
          </div>
          <div className="mx-4 block md:mx-0 lg:hidden lg:max-w-[66%]">
            <SwiperCustom
              scrollbar
              navigation
              slidesPerView={3}
              slidesPerViewMobile={1.3}
              gap={24}
            >
              {blogProps?.map((blog, index) => (
                <CardBlog {...blog} key={index} className="h-full" />
              ))}
            </SwiperCustom>
          </div>
        </>
      )}

      <>
        {link?.uri && link?.label && (
          <NextLink passHref prefetch={false} href={link.uri}>
            <Link
              underline
              arrow
              className="self-center lg:hidden"
              href={link.uri}
              variant={link.variant}
            >
              {link.label}
            </Link>
          </NextLink>
        )}
      </>
    </div>
  </Container>
);

CardsBlogTemplate.displayName = 'CardsBlogTemplate';
